import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CONTEXT_MODE, useLensIntelligenceScope } from '../../hooks/useLensIntelligenceScope';
import { useLensIntelligenceCitationPanels } from '../../hooks/useLensIntelligenceCitationPanels';
import useVdrIndexStatusPolling from '../../hooks/useVdrIndexStatusPolling';
import { dispatchScopedRagAsk } from '../../utils/ragDispatch';
import {
    clearAskAiChat,
    fetchIntelligenceConversations,
    fetchIntelligenceMessages,
    fetchIntelligenceScopeCatalog,
    isIntelligenceAskEnabled,
    upsertConversationLocal,
    appendOptimisticUserMessage,
    appendLocalSystemMessage,
    appendLocalAssistantTurn,
    postIntelligenceContextChange,
    setInsightsAskOrigin,
    bindInsightsAskOriginConversation,
} from '../../redux/intelligenceSlice';
import { workstreamOptionsFromList } from '../../utils/aiWardenScope';
import { contextChangeMessageText, contextModeLabel } from '../../utils/askAiChatMeta';
import { countIntelligenceSourceReadiness } from '../../utils/intelligenceSourceReadiness';
import {
    normalizeInsightsAskLensOrigin,
    originFromConversation,
    serializeInsightsOriginForAsk,
    readInsightsModuleAskContext,
    storeInsightsModuleAskContext,
} from '../../utils/intelligenceInsightsAskLensOrigin';
import { activeIndexingFileIds, failedIndexingFileIds } from '../../utils/vdrIndexingQueue';
import { markFilesIndexed } from '../../redux/documentsSlice';
import LensIntelligenceShell from './LensIntelligenceShell';
import AskAiChatSidebar from './AskAiChatSidebar';
import AskAiEmptyState from './AskAiEmptyState';
import AskAiChatThread from './AskAiChatThread';
import AskAiSavedPromptsModal from './AskAiSavedPromptsModal';
import AskAiChooseContextModal from './AskAiChooseContextModal';
import LensIntelligenceCitationSidePanel from './LensIntelligenceCitationSidePanel';
import { useStreamingResponse } from './streaming';
import styles from './askAi.module.css';

function chooseContextLabel(mode, selectedWorkstreams) {
    if (mode === CONTEXT_MODE.CUSTOM) return 'Custom Sources';
    if (mode === CONTEXT_MODE.SELECT_WORKSTREAMS) {
        const n = Array.isArray(selectedWorkstreams) ? selectedWorkstreams.length : 0;
        if (n <= 0) return 'Workstreams';
        if (n === 1) return 'Selected workstream';
        return `${n} workstreams`;
    }
    return contextModeLabel('entire_project');
}

const CATALOG_REFRESH_WHILE_PROCESSING_MS = 12000;

const LensIntelligence = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedProject } = useSelector(state => state.projectData);
    const projectId = selectedProject?.id;
    const indexingFiles = useSelector(state => state?.documents?.indexingFiles || []);

    // Keep VDR indexing status fresh while on Ask Lens (same poller as Dataroom).
    useVdrIndexStatusPolling();

    const {
        conversations = [],
        conversationsLoading = false,
        activeConversationId = null,
        messages = [],
        messagesLoading = false,
        insightsAskOrigin = null,
        insightsAskOriginByConversationId = {},
    } = useSelector(state => state.intelligence) || {};

    const {
        contextMode,
        setContextMode,
        selectedWorkstreams,
        setSelectedWorkstreams,
        customContext,
        setCustomContext,
        workstreams,
        effectiveWorkstreamIds,
        scopeStats,
        modalFiles,
        resolveScope,
        citationDisplayCtx,
        scopeCatalog,
    } = useLensIntelligenceScope(projectId);

    const { sidePanelOpen, closeAllPanels, handleInlineCitationClick, openCitationFromEvidenceItem, panelProps } = useLensIntelligenceCitationPanels({
        modalFiles,
        effectiveWorkstreamIds,
        citationDisplayCtx,
    });

    const [question, setQuestion] = useState('');
    const [contextModalOpen, setContextModalOpen] = useState(false);
    const [savedPromptsOpen, setSavedPromptsOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [askPipelineScope, setAskPipelineScope] = useState(null);
    const {
        state: streamingState,
        begin: beginStreaming,
        streamResponse,
        cancel: cancelStreaming,
    } = useStreamingResponse();

    const indexingFileIds = useMemo(
        () => activeIndexingFileIds(indexingFiles, { projectId }),
        [indexingFiles, projectId],
    );
    const failedIndexFileIds = useMemo(
        () => failedIndexingFileIds(indexingFiles, { projectId }),
        [indexingFiles, projectId],
    );

    const sourceReadiness = useMemo(
        () =>
            countIntelligenceSourceReadiness({
                documents: scopeCatalog?.documents || [],
                irls: scopeCatalog?.irls || [],
                observations: scopeCatalog?.observations || [],
                indexingFileIds,
            }),
        [scopeCatalog, indexingFileIds],
    );

    const sourcesReady = useMemo(() => {
        if (sourceReadiness.readyTotal > 0) return sourceReadiness.readyTotal;
        const docs = Number(scopeStats?.documents) || 0;
        const irl = Number(scopeStats?.irlResponses) || 0;
        const obs = Number(scopeStats?.observations) || 0;
        return docs + irl + obs;
    }, [sourceReadiness.readyTotal, scopeStats]);

    const sourcesProcessing = sourceReadiness.processingTotal;

    // Quietly refresh catalog while VDR files are still indexing (no "ready" toast).
    useEffect(() => {
        if (!projectId || sourcesProcessing <= 0) return undefined;
        const id = window.setInterval(() => {
            dispatch(fetchIntelligenceScopeCatalog({ projectId }));
        }, CATALOG_REFRESH_WHILE_PROCESSING_MS);
        return () => window.clearInterval(id);
    }, [projectId, sourcesProcessing, dispatch]);

    // When local VDR indexing queue changes, refresh catalog counts (still no toast).
    useEffect(() => {
        if (!projectId || indexingFileIds.length === 0) return undefined;
        dispatch(fetchIntelligenceScopeCatalog({ projectId }));
        return undefined;
    }, [projectId, indexingFileIds.length, dispatch]);

    // Drop stale local indexing entries once the catalog reports those files ready.
    useEffect(() => {
        const localIds = [...indexingFileIds, ...failedIndexFileIds];
        if (!localIds.length) return;
        const doneIds = (scopeCatalog?.documents || [])
            .filter(doc => {
                const status = String(doc?.indexing_status || '')
                    .toLowerCase()
                    .trim();
                return status === 'ready' || status === 'indexed';
            })
            .map(doc => Number(doc?.id ?? doc?.file_id))
            .filter(id => Number.isFinite(id) && localIds.includes(id));
        if (doneIds.length) {
            dispatch(markFilesIndexed(doneIds));
        }
    }, [scopeCatalog, indexingFileIds, failedIndexFileIds, dispatch]);

    useEffect(() => {
        if (!projectId) return;
        // Local Ask Lens UI + citations belong to the previous project until reset.
        setQuestion('');
        setSubmitting(false);
        setAskPipelineScope(null);
        setContextModalOpen(false);
        setSavedPromptsOpen(false);
        closeAllPanels();
        dispatch(fetchIntelligenceConversations({ projectId }));
    }, [projectId, dispatch, closeAllPanels]);

    useEffect(() => {
        const incoming = location.state?.insightsAskOrigin;
        if (incoming) {
            const normalized = {
                ...normalizeInsightsAskLensOrigin(incoming),
                openedAt: incoming.openedAt || Date.now(),
            };
            const origin = storeInsightsModuleAskContext(
                normalized,
                readInsightsModuleAskContext(incoming) || readInsightsModuleAskContext(normalized),
            );
            cancelStreaming();
            dispatch(clearAskAiChat());
            dispatch(setInsightsAskOrigin(origin));
            setQuestion('');
            setAskPipelineScope(null);
            navigate(location.pathname, { replace: true, state: {} });
            return;
        }

        // Ask Lens opened normally (tab, sidebar, etc.) — do not keep a stale Insights module context
        // from a previous handoff on the empty new-chat screen.
        if (!activeConversationId && insightsAskOrigin) {
            dispatch(setInsightsAskOrigin(null));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleContextModeChange = useCallback(
        mode => {
            setContextMode(mode);
            if (mode === CONTEXT_MODE.ENTIRE_PROJECT) {
                setSelectedWorkstreams(workstreamOptionsFromList(workstreams));
            } else if (mode === CONTEXT_MODE.SELECT_WORKSTREAMS && !selectedWorkstreams?.length) {
                // Keep prior selection when switching; empty selection blocks ask.
                setSelectedWorkstreams(workstreamOptionsFromList(workstreams));
            }
        },
        [setContextMode, setSelectedWorkstreams, workstreams, selectedWorkstreams],
    );

    const handleCustomSelect = useCallback(() => {
        setContextModalOpen(true);
    }, []);

    const handleOpenChangeContext = useCallback(() => {
        setContextModalOpen(true);
    }, []);

    const handleApplyChooseContext = useCallback(
        ({ mode, selectedWorkstreams: nextWs, customContext: nextCustom }) => {
            const hadChatHistory = (messages || []).some(m => m.role === 'user' || m.role === 'assistant');
            const label = chooseContextLabel(mode, nextWs);

            if (mode === CONTEXT_MODE.CUSTOM) {
                setCustomContext(nextCustom);
                setContextMode(CONTEXT_MODE.CUSTOM);
            } else if (mode === CONTEXT_MODE.SELECT_WORKSTREAMS) {
                setSelectedWorkstreams(nextWs || []);
                setContextMode(CONTEXT_MODE.SELECT_WORKSTREAMS);
            } else {
                setSelectedWorkstreams(nextWs?.length ? nextWs : workstreamOptionsFromList(workstreams));
                setContextMode(CONTEXT_MODE.ENTIRE_PROJECT);
            }
            setContextModalOpen(false);

            if (!hadChatHistory) return;

            const content = contextChangeMessageText(label);
            const scope_json = {
                mode,
                label,
                workstream_ids: Array.isArray(nextWs) ? nextWs.map(w => w?.id ?? w).filter(Boolean) : [],
            };

            if (projectId && activeConversationId) {
                dispatch(
                    appendLocalSystemMessage({
                        content,
                        meta: { type: 'context_change', context_label: label },
                    }),
                );
                void dispatch(
                    postIntelligenceContextChange({
                        projectId,
                        conversationId: activeConversationId,
                        label,
                        scope_json,
                    }),
                )
                    .unwrap()
                    .catch(() => {
                        toast.error('Could not save context change in chat history.');
                    });
            } else {
                dispatch(
                    appendLocalSystemMessage({
                        content,
                        meta: { type: 'context_change', context_label: label },
                    }),
                );
            }
        },
        [messages, setCustomContext, setContextMode, setSelectedWorkstreams, workstreams, projectId, activeConversationId, dispatch],
    );

    const handleNewChat = useCallback(() => {
        cancelStreaming();
        dispatch(clearAskAiChat());
        dispatch(setInsightsAskOrigin(null));
        setQuestion('');
        setAskPipelineScope(null);
    }, [dispatch, cancelStreaming]);

    const finalizeAskExchange = useCallback(
        async (ragResult, questionText) => {
            await streamResponse(ragResult.data);
            const conversationId = ragResult.data?.conversation_id || activeConversationId || null;
            if (!conversationId) return;

            if (insightsAskOrigin) {
                dispatch(bindInsightsAskOriginConversation(conversationId));
            }

            dispatch(
                upsertConversationLocal({
                    id: conversationId,
                    title: ragResult.data?.conversation_title || questionText.slice(0, 80),
                    updated_at: new Date().toISOString(),
                    exchange_count: Math.ceil(((messages?.length || 0) + 2) / 2),
                    source_count: Array.isArray(ragResult.data?.citations)
                        ? ragResult.data.citations.length
                        : 0,
                    scope_json: insightsAskOrigin
                        ? { insights_origin: serializeInsightsOriginForAsk(insightsAskOrigin) }
                        : undefined,
                }),
            );

            if (ragResult.usedRagFallback) {
                // Intelligence may have persisted a list_report redirect stub; keep the
                // rag_chat answer that was just streamed instead of refetching messages.
                dispatch(
                    appendLocalAssistantTurn({
                        question: questionText,
                        content: ragResult.data?.answer || ragResult.data?.final_answer || '',
                        citations: ragResult.data?.citations || [],
                        evidence: ragResult.data?.evidence || [],
                        confidence: ragResult.data?.confidence || '',
                        conversationId,
                        meta: {
                            retrieval_debug: {
                                ...(ragResult.data?.retrieval_debug || {}),
                                rag_chat_fallback: true,
                            },
                            response_blocks:
                                ragResult.data?.response_blocks || ragResult.data?.blocks || [],
                            suggested_actions: ragResult.data?.suggested_actions || [],
                            response_actions: ragResult.data?.response_actions || null,
                        },
                    }),
                );
                dispatch(fetchIntelligenceConversations({ projectId }));
                return;
            }

            await dispatch(fetchIntelligenceMessages({ projectId, conversationId })).unwrap();
            dispatch(fetchIntelligenceConversations({ projectId }));
        },
        [activeConversationId, dispatch, insightsAskOrigin, messages, projectId, streamResponse],
    );

    const handleSelectChat = useCallback(
        async conversationId => {
            if (!projectId || !conversationId) return;
            setQuestion('');
            const conv = (conversations || []).find(c => String(c.id) === String(conversationId));
            const fromMap = insightsAskOriginByConversationId?.[conversationId];
            const fromConv = originFromConversation(conv);
            const nextOrigin = fromMap || fromConv || null;
            dispatch(
                setInsightsAskOrigin(
                    nextOrigin ? { ...nextOrigin, conversationId } : null,
                ),
            );
            try {
                await dispatch(fetchIntelligenceMessages({ projectId, conversationId })).unwrap();
            } catch (e) {
                toast.error(e?.detail || 'Failed to load chat.');
            }
        },
        [conversations, dispatch, insightsAskOriginByConversationId, projectId],
    );

    const handleSubmit = useCallback(async () => {
        if (!projectId) return toast.error('No project selected.');
        const q = question.trim();
        if (!q) return toast.error('Enter a question.');

        const scope = resolveScope();
        if (!scope.workstreamIds?.length) {
            return toast.error(contextMode === CONTEXT_MODE.CUSTOM ? 'Configure custom context with at least one workstream.' : 'Select at least one workstream.');
        }

        setQuestion('');
        dispatch(appendOptimisticUserMessage({ content: q }));
        beginStreaming();
        setSubmitting(true);
        setAskPipelineScope({
            type: scope.type,
            workstreamIds: scope.workstreamIds,
            fileIds: scope.fileIds,
            folderIds: scope.folderIds,
            label: scope.label,
            includeVdr: scope.includeVdr,
            includeIrl: scope.includeIrl,
            includeObservations: scope.includeObservations,
        });

        try {
            const body = {
                question: q,
                top: 8,
                workstream_ids: scope.workstreamIds,
            };

            const ragResult = await dispatchScopedRagAsk({
                dispatch,
                projectId,
                wsId: scope.workstreamIds?.length === 1 ? scope.workstreamIds[0] : undefined,
                scope,
                body,
                preferIntelligence: true,
                accuracyMode: true,
                conversationId: activeConversationId || null,
                persistConversation: true,
                insightsOrigin: insightsAskOrigin,
            });

            await finalizeAskExchange(ragResult, q);
        } catch (e) {
            const msg = e?.detail || e?.error || (typeof e === 'string' ? e : 'Request failed');
            toast.error(typeof msg === 'string' ? msg : 'Request failed');
        } finally {
            setSubmitting(false);
            cancelStreaming();
            setAskPipelineScope(null);
        }
    }, [projectId, question, resolveScope, contextMode, dispatch, activeConversationId, beginStreaming, finalizeAskExchange, cancelStreaming, insightsAskOrigin]);

    const askSuggested = useCallback(
        async q => {
            if (!projectId) return toast.error('No project selected.');
            const scope = resolveScope();
            if (!scope.workstreamIds?.length) {
                return toast.error('Select at least one workstream.');
            }
            setQuestion('');
            dispatch(appendOptimisticUserMessage({ content: q }));
            beginStreaming();
            setSubmitting(true);
            setAskPipelineScope({
                type: scope.type,
                workstreamIds: scope.workstreamIds,
                fileIds: scope.fileIds,
                folderIds: scope.folderIds,
                label: scope.label,
                includeVdr: scope.includeVdr,
                includeIrl: scope.includeIrl,
                includeObservations: scope.includeObservations,
            });
            try {
                const ragResult = await dispatchScopedRagAsk({
                    dispatch,
                    projectId,
                    wsId: scope.workstreamIds?.length === 1 ? scope.workstreamIds[0] : undefined,
                    scope,
                    body: { question: q, top: 8, workstream_ids: scope.workstreamIds },
                    preferIntelligence: true,
                    accuracyMode: true,
                    conversationId: activeConversationId || null,
                    persistConversation: true,
                    insightsOrigin: insightsAskOrigin,
                });
                await finalizeAskExchange(ragResult, q);
            } catch (e) {
                const msg = e?.detail || e?.error || 'Request failed';
                toast.error(typeof msg === 'string' ? msg : 'Request failed');
            } finally {
                setSubmitting(false);
                cancelStreaming();
                setAskPipelineScope(null);
            }
        },
        [projectId, resolveScope, dispatch, activeConversationId, beginStreaming, finalizeAskExchange, cancelStreaming, insightsAskOrigin],
    );

    const showThread =
        Boolean(activeConversationId) ||
        (messages?.length || 0) > 0 ||
        submitting ||
        streamingState.active;

    return (
        <LensIntelligenceShell activeTab="ask" sourcesReady={sourcesReady} sourcesProcessing={sourcesProcessing}>
            <div className={styles.askPage}>
                <AskAiChatSidebar
                    conversations={conversations}
                    loading={conversationsLoading}
                    activeConversationId={activeConversationId}
                    onNewChat={handleNewChat}
                    onSelectChat={id => void handleSelectChat(id)}
                    onOpenSavedPrompts={() => setSavedPromptsOpen(true)}
                />

                <div className={styles.workspace}>
                    {!showThread ? (
                        <AskAiEmptyState
                            question={question}
                            onQuestionChange={setQuestion}
                            contextMode={contextMode}
                            onContextModeChange={handleContextModeChange}
                            onCustomSelect={handleCustomSelect}
                            onOpenChangeContext={handleOpenChangeContext}
                            onOpenSavedPrompts={() => setSavedPromptsOpen(true)}
                            onSubmit={() => void handleSubmit()}
                            submitting={submitting}
                            sourcesReady={sourcesReady}
                            sourcesProcessing={sourcesProcessing}
                            onSuggestedQuestion={q => void askSuggested(q)}
                            insightsOrigin={insightsAskOrigin}
                        />
                    ) : (
                        <AskAiChatThread
                            messages={messages}
                            messagesLoading={messagesLoading}
                            submitting={submitting}
                            streamingState={streamingState}
                            question={question}
                            onQuestionChange={setQuestion}
                            contextMode={contextMode}
                            onContextModeChange={handleContextModeChange}
                            onCustomSelect={handleCustomSelect}
                            onOpenChangeContext={handleOpenChangeContext}
                            onOpenSavedPrompts={() => setSavedPromptsOpen(true)}
                            onSubmit={() => void handleSubmit()}
                            citationDisplayCtx={citationDisplayCtx}
                            onInlineCitationClick={handleInlineCitationClick}
                            onEvidenceItemClick={openCitationFromEvidenceItem}
                            sourcesRailOpen={sidePanelOpen}
                            sourcesReady={sourcesReady}
                            sourcesProcessing={sourcesProcessing}
                            sourcesRail={sidePanelOpen ? <LensIntelligenceCitationSidePanel panelProps={panelProps} /> : null}
                            insightsOrigin={insightsAskOrigin}
                        />
                    )}
                </div>

                {sidePanelOpen && !showThread ? <LensIntelligenceCitationSidePanel panelProps={panelProps} /> : null}
            </div>

            <AskAiChooseContextModal
                open={contextModalOpen}
                onClose={() => setContextModalOpen(false)}
                onApply={handleApplyChooseContext}
                initialMode={contextMode}
                initialWorkstreams={selectedWorkstreams}
                initialCustomContext={customContext}
                workstreams={workstreams}
                projectId={projectId}
                scopeStats={scopeStats}
                indexingFiles={indexingFiles}
            />

            <AskAiSavedPromptsModal open={savedPromptsOpen} onClose={() => setSavedPromptsOpen(false)} onUsePrompt={text => setQuestion(text)} />
        </LensIntelligenceShell>
    );
};

export default LensIntelligence;
